/**
 * PlaybackControls Component
 *
 * Audio playback controls with play/pause, time display, speed, volume, and zoom.
 */

import { Button, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Slider } from '@heroui/react';
import {
  Pause,
  Play,
  Volume2,
  VolumeX,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';

import { formatDuration } from '@/lib/design-system';

interface PlaybackControlsProps {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  playbackRate: number;
  volume: number;
  zoom: number;
  disabled?: boolean;
  onPlayPause: () => void;
  onPlaybackRateChange: (rate: number) => void;
  onVolumeChange: (volume: number) => void;
  onZoomChange: (zoom: number) => void;
  onSeek: (time: number) => void;
}

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];
const MIN_ZOOM = 20;
const MAX_ZOOM = 500;
const ZOOM_STEP = 50;

export function PlaybackControls({
  isPlaying,
  currentTime,
  duration,
  playbackRate,
  volume,
  zoom,
  disabled = false,
  onPlayPause,
  onPlaybackRateChange,
  onVolumeChange,
  onZoomChange,
  onSeek,
}: PlaybackControlsProps) {
  const handleZoomIn = () => {
    onZoomChange(Math.min(zoom + ZOOM_STEP, MAX_ZOOM));
  };

  const handleZoomOut = () => {
    onZoomChange(Math.max(zoom - ZOOM_STEP, MIN_ZOOM));
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (duration <= 0) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    onSeek(newTime);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="flex flex-col gap-3">
      {/* Progress bar */}
      <div
        className="group relative h-2 rounded-full bg-default-200 cursor-pointer"
        onClick={handleProgressClick}
      >
        <div
          className="absolute left-0 top-0 h-full rounded-full bg-primary transition-all"
          style={{ width: `${progress}%` }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-primary opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ left: `calc(${progress}% - 8px)` }}
        />
      </div>

      {/* Controls row */}
      <div className="flex items-center justify-between">
        {/* Left: Play/Pause + Time */}
        <div className="flex items-center gap-3">
          <Button
            isIconOnly
            variant="flat"
            size="sm"
            onPress={onPlayPause}
            isDisabled={disabled}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <Pause className="h-4 w-4" />
            ) : (
              <Play className="h-4 w-4" />
            )}
          </Button>

          <span className="text-sm font-mono text-default-600 min-w-[100px]">
            {formatDuration(currentTime * 1000)} / {formatDuration(duration * 1000)}
          </span>
        </div>

        {/* Right: Speed + Volume + Zoom */}
        <div className="flex items-center gap-2">
          {/* Playback Speed */}
          <Dropdown>
            <DropdownTrigger>
              <Button
                variant="flat"
                size="sm"
                className="min-w-[60px]"
                isDisabled={disabled}
              >
                {playbackRate}x
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Playback speed"
              selectionMode="single"
              selectedKeys={[String(playbackRate)]}
              onSelectionChange={(keys) => {
                const rate = parseFloat(Array.from(keys)[0] as string);
                if (!isNaN(rate)) {
                  onPlaybackRateChange(rate);
                }
              }}
            >
              {PLAYBACK_RATES.map((rate) => (
                <DropdownItem key={String(rate)}>
                  {rate}x
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>

          {/* Volume */}
          <div className="flex items-center gap-1 group">
            <Button
              isIconOnly
              variant="light"
              size="sm"
              onPress={() => onVolumeChange(volume > 0 ? 0 : 1)}
              isDisabled={disabled}
              aria-label={volume > 0 ? 'Mute' : 'Unmute'}
            >
              {volume > 0 ? (
                <Volume2 className="h-4 w-4" />
              ) : (
                <VolumeX className="h-4 w-4" />
              )}
            </Button>
            <div className="w-0 overflow-hidden group-hover:w-20 transition-all">
              <Slider
                aria-label="Volume"
                size="sm"
                step={0.1}
                minValue={0}
                maxValue={1}
                value={volume}
                onChange={(value) => onVolumeChange(value as number)}
                isDisabled={disabled}
                className="w-20"
                classNames={{
                  track: 'h-1',
                  filler: 'bg-default-400',
                  thumb: 'w-3 h-3',
                }}
              />
            </div>
          </div>

          {/* Zoom */}
          <div className="flex items-center gap-1 border-l border-default-200 pl-2">
            <Button
              isIconOnly
              variant="light"
              size="sm"
              onPress={handleZoomOut}
              isDisabled={disabled || zoom <= MIN_ZOOM}
              aria-label="Zoom out"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <Button
              isIconOnly
              variant="light"
              size="sm"
              onPress={handleZoomIn}
              isDisabled={disabled || zoom >= MAX_ZOOM}
              aria-label="Zoom in"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
